import { useEffect, useMemo } from 'react';
import { skipToken, useQuery } from '@apollo/client/react';

import {
    IUserListItemFragment,
    IUserSelectorInput,
    IUserSelectorQueryVariables,
    UserSelectorDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';

export interface IUserList {
    users: IUserListItemFragment[];
    alreadyAddedUsers: string[];
    usersCount?: number | null;
    loading: boolean;
}

export const useUserList = (
    userQueryVariables?: IUserSelectorQueryVariables,
    skip?: boolean,
    selectorQueryVariables?: IUserSelectorInput
): IUserList => {
    const { showApolloError } = useApolloError();
    const shouldFetch = !!userQueryVariables && !skip;

    const { loading, data, error } = useQuery(
        UserSelectorDocument,
        shouldFetch
            ? {
                  variables: {
                      q: userQueryVariables.q,
                      offset: userQueryVariables.offset,
                      ...userQueryVariables,
                      selector: selectorQueryVariables,
                  },
              }
            : skipToken
    );

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const usersObject = data?.paginatedUsers;
    const usersCount = usersObject?.count;
    const users = useMemo(
        () =>
            (usersObject?.edges
                .map((edge) => edge?.node || null)
                .filter(Boolean) as IUserListItemFragment[]) || [],
        [usersObject?.edges]
    );
    const alreadyAddedUsers = useMemo(
        () =>
            usersObject?.edges
                .map((edge) => {
                    if (edge?.alreadyAddedToObject) return edge.node.id;

                    return null;
                })
                .filter(Boolean) || [],
        [usersObject?.edges]
    );

    return {
        users,
        alreadyAddedUsers,
        usersCount,
        loading,
    };
};
